import React, { useEffect, useState, useCallback } from 'react';
import { FiBarChart, FiFileText, FiRefreshCcw } from 'react-icons/fi';
import { BsGeo, BsFileEarmarkExcel, BsTable, BsFileText } from 'react-icons/bs';
import ViewMaintenanceContractModal from '../Maintenance Contract Entry/ViewMaintenanceContractModal'; // Adjust the import path accordingly
import { maintenanceContractFields } from '../Maintenance Contract Entry/maintenanceContractFields'; // Ensure this path is correct
import API_BASE_URL from '../../../../Constants';

const MaintenanceDashboard = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);

  // Fetch all maintenance contracts
  const fetchContracts = useCallback(async () => {
    try {
      const response = await fetch(API_BASE_URL + `/api/maintenance/maintenance-contracts?size=1000`, {
        credentials: 'include',
      });
      const data = await response.json();
      setContracts(Array.isArray(data.content) ? data.content : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleOpenViewModal = (id) => {
    setSelectedContractId(id);
    setIsViewOpen(true);
  };

  const handleCloseViewModal = () => {
    setSelectedContractId(null);
    setIsViewOpen(false);
  };

  // Separate contracts into added and lost based on status
  const contractsAdded = contracts.filter(contract => contract.status === 'added').slice(0, 10);
  const contractsLost = contracts.filter(contract => contract.status === 'lost').slice(0, 10);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  return (
    <div className="w-full lg:w-4/5 mx-auto p-6 bg-white rounded-3xl shadow-lg flex flex-col lg:flex-row border border-gray-300">
      {/* Main Content Area */}
      <div className="flex-1 lg:pr-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Maintenance Dashboard</h2>
          <button
            onClick={fetchContracts}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md"
          >
            <FiRefreshCcw className="mr-2" />
            Refresh
          </button>
        </div>

        {/* Top Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-100 p-4 rounded-lg shadow-md text-center">
            <h3 className="font-semibold text-gray-700">Added in Month</h3>
            <p className="text-2xl font-bold">{contractsAdded.length}</p>
          </div>
          <div className="bg-red-100 p-4 rounded-lg shadow-md text-center">
            <h3 className="font-semibold text-gray-700">Lost in Month</h3>
            <p className="text-2xl font-bold">{contractsLost.length}</p>
          </div>
          <div className="bg-green-100 p-4 rounded-lg shadow-md text-center">
            <h3 className="font-semibold text-gray-700">YTD Added</h3>
            <p className="text-2xl font-bold">50</p> {/* Replace with real data if available */}
          </div>
          <div className="bg-blue-100 p-4 rounded-lg shadow-md text-center">
            <h3 className="font-semibold text-gray-700">YTD Lost</h3>
            <p className="text-2xl font-bold">25</p> {/* Replace with real data if available */}
          </div>
        </div>

        {/* Contracts Added Table */}
        <div className="mb-6 border border-gray-300 p-6 rounded-3xl shadow-md overflow-x-auto">
          <h3 className="font-semibold mb-4 text-2xl">Contracts Added</h3>
          <table className="min-w-full bg-white rounded-lg shadow-lg">
            <thead className="bg-gray-100">
              <tr>
                {/* Actions Column First */}
                <th className="px-4 py-2 text-left font-bold text-black">Actions</th>
                {maintenanceContractFields && maintenanceContractFields.length > 0 && maintenanceContractFields.map((field) => (
                  <th key={field.name} className="px-4 py-2 text-left font-bold text-black">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {contractsAdded.length > 0 ? (
                contractsAdded.map((contract) => (
                  <tr key={contract.contractId} className="border-t hover:bg-gray-50">
                    {/* Actions Column */}
                    <td className="px-4 py-2">
                      <button
                        className="text-blue-500 underline"
                        onClick={() => handleOpenViewModal(contract.contractId)}
                      >
                        View
                      </button>
                    </td>
                    {maintenanceContractFields.map((field) => (
                      <td key={field.name} className="px-4 py-2">
                        {contract[field.name] || 'N/A'}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={maintenanceContractFields.length + 1} className="p-4 text-center text-gray-500">
                    No contracts added this month.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Contracts Lost Table */}
        <div className="mb-6 border border-gray-300 p-6 rounded-3xl shadow-md overflow-x-auto">
          <h3 className="font-semibold mb-4 text-2xl">Contracts Lost</h3>
          <table className="min-w-full bg-white rounded-lg shadow-lg">
            <thead className="bg-gray-100">
              <tr>
                {/* Actions Column First */}
                <th className="px-4 py-2 text-left font-bold text-black">Actions</th>
                {maintenanceContractFields && maintenanceContractFields.length > 0 && maintenanceContractFields.map((field) => (
                  <th key={field.name} className="px-4 py-2 text-left font-bold text-black">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {contractsLost.length > 0 ? (
                contractsLost.map((contract) => (
                  <tr key={contract.contractId} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <button
                        className="text-blue-500 underline"
                        onClick={() => handleOpenViewModal(contract.contractId)}
                      >
                        View
                      </button>
                    </td>
                    {maintenanceContractFields.map((field) => (
                      <td key={field.name} className="px-4 py-2">
                        {contract[field.name] || 'N/A'}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={maintenanceContractFields.length + 1} className="p-4 text-center text-gray-500">
                    No contracts lost this month.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sidebar Buttons */}
      <div className="w-full lg:w-1/5 mt-6 lg:mt-0 p-4 border border-gray-300 rounded-3xl bg-gray-100 shadow-lg">
        <div className="flex flex-col gap-4">
          <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md">
            <FiRefreshCcw className="mr-2" />
            Refresh
          </button>
          <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md">
            <BsFileEarmarkExcel className="mr-2" />
            Excel
          </button>
          <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center shadow-md">
            <BsFileText className="mr-2" />
            Reports
          </button>
          <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md">
            <FiBarChart className="mr-2" />
            Summary
          </button>
          <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md">
            <BsTable className="mr-2" />
            Log
          </button>
          <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md">
            <BsGeo className="mr-2" />
            Planning
          </button>
          <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg flex items-center shadow-md">
            <FiFileText className="mr-2" />
            Analysis
          </button>
        </div>
      </div>

      {/* View Maintenance Contract Modal */}
      {selectedContractId && (
        <ViewMaintenanceContractModal
          open={isViewOpen}
          handleClose={handleCloseViewModal}
          contractId={selectedContractId}
          refreshContracts={fetchContracts}
        />
      )}
    </div>
  );
};

export default MaintenanceDashboard;
